import JsonField from '@/components/jsonField';
import { useElementSize } from '@custom-react-hooks/use-element-size';
import { Box, Button, Stack, alpha, useTheme } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  questions: Record<string, any>;
  userResponses: Record<string, any>;
  maxHeight: number;
  onParsedRequirementsChange: (questionId: string, response: any) => void;
  onBack: () => void;
  onCancel: () => void;
  onNext: () => void;
};

const StepJsonFile = ({
  questions = {},
  userResponses = {},
  maxHeight,
  onParsedRequirementsChange,
  onBack,
  onCancel,
  onNext,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [containerRef, containerSize] = useElementSize();
  const [actionPanelRef, actionPanelSize] = useElementSize();

  const [errors, setErrors] = useState<any>({});

  const setJsonSchema = (response: string) => {
    onParsedRequirementsChange('jsonSchema', response);
  };

  const setJsonSample = (response: string) => {
    onParsedRequirementsChange('jsonSample', response);
  };

  const handleNext = () => {
    const errors: any = {};
    let hasError = false;
    if (userResponses.parsedRequirements.extractionType === 'jsonSchema') {
      if (!userResponses.parsedRequirements.jsonSchema) {
        errors.jsonSchema = t('common.errors.requiredField');
        hasError = true;
      }
    }

    if (userResponses.parsedRequirements.extractionType === 'jsonSample') {
      if (!userResponses.parsedRequirements.jsonSample) {
        errors.jsonSample = t('common.errors.requiredField');
        hasError = true;
      }
    }

    setErrors(errors);
    if (!hasError) {
      onNext();
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleBack = () => {
    onBack();
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        height: `${maxHeight}px`,
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderLeft: `3px solid ${theme.palette.secondary.main}`,
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        <Stack alignItems={'flex-start'} spacing={4}>
          {userResponses.parsedRequirements.extractionType === 'jsonSchema' && (
            <JsonField
              label={questions.jsonSchema.question}
              value={userResponses.parsedRequirements.jsonSchema || ''}
              required
              error={errors.jsonSchema}
              rows={5}
              onChange={setJsonSchema}
            />
          )}
          {userResponses.parsedRequirements.extractionType === 'jsonSample' && (
            <JsonField
              label={questions.jsonSample.question}
              value={userResponses.parsedRequirements.jsonSample || ''}
              required
              error={errors.jsonSample}
              rows={5}
              onChange={setJsonSample}
              isJsonSchema={false}
            />
          )}
        </Stack>
      </Box>
      <Box
        ref={actionPanelRef}
        sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.02) }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
          >
            {t('common.buttons.back')}
          </Button>
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="text"
              onClick={handleCancel}
              sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
            >
              {t('common.buttons.cancel')}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNext}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '10px',
                px: 2.5,
                color: 'text.primary',
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              {t('common.buttons.next')}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default StepJsonFile;
